/**
 * One-shot "show the welcome modal" flag.
 *
 * The onboarding wizard ends with a full page load to `/`, so the signal to
 * greet the user has to survive that reload: it lives in sessionStorage,
 * which is per-tab and short-lived. The home page consumes (reads and clears)
 * it on mount, so a later reload or revisit never shows the modal again.
 *
 * Storage access can throw (privacy modes, quota, sandboxed iframes); every
 * helper swallows that so onboarding and the home page never break on it.
 */
export const WELCOME_PENDING_KEY = 'specificai.welcome';

const storage = () => {
    try {
        return globalThis.sessionStorage || null;
    } catch {
        return null;
    }
};

/** Flag the next home-page visit to show the welcome modal. */
export function markWelcomePending() {
    try {
        storage()?.setItem(WELCOME_PENDING_KEY, '1');
    } catch {
        // Nothing to do — the modal is a nicety, not a requirement.
    }
}

/**
 * Read and clear the flag. Returns `true` exactly once after
 * `markWelcomePending()`; `false` when unset or when storage is unavailable.
 */
export function consumeWelcomePending() {
    try {
        const store = storage();
        if (!store || store.getItem(WELCOME_PENDING_KEY) !== '1') return false;
        store.removeItem(WELCOME_PENDING_KEY);
        return true;
    } catch {
        return false;
    }
}
